/**
 * Chart geometry: the mapping between data and pixels, and the pan/zoom state
 * that drives it.
 *
 * The x axis is **index space**, not time space. Candles are laid out by
 * ordinal position, so a market that skipped a bar (a delisting halt, a gap in
 * our own data after a disconnect) does not open a hole in the chart, and
 * candle width is constant regardless of interval. Anything anchored in real
 * time — a drawing, an alert, the time axis labels — converts through the
 * series' `fractionalIndex` / `timeAtIndex` pair.
 *
 * Everything here is plain arithmetic on a mutable object. It is called
 * hundreds of times per frame per panel, so there are no allocations and no
 * getters doing work.
 */

import { clamp, niceStep } from '../lib/num';
import type { KlineSeries } from '../data/klines';

export interface Insets {
  /** Width of the right-hand price axis gutter. */
  right: number;
  /** Height of the bottom time axis. */
  bottom: number;
  top: number;
  left: number;
}

/** The right gutter must fit the widest price label a symbol can produce. */
export const DEFAULT_INSETS: Insets = { right: 76, bottom: 24, top: 8, left: 0 };

/** Fraction of the plot height given to the volume pane. */
export const VOLUME_FRACTION = 0.2;

export class ChartViewport {
  /** Fractional bar index at the left edge of the plot area. */
  offset = 0;
  /** How many bars span the plot width. Zoom is a change to this. */
  barsVisible = 140;

  /** Total canvas size in CSS pixels. */
  width = 0;
  height = 0;
  insets: Insets = { ...DEFAULT_INSETS };

  /** Set false as soon as the user pans away from the right edge. */
  following = true;

  logScale = false;
  autoScale = true;

  /** Visible price range, recomputed each frame when autoScale is on. */
  priceMin = 0;
  priceMax = 1;
  /** Manual range, used when the user has dragged the price axis. */
  private manualMin = 0;
  private manualMax = 0;

  /** Extra room for an indicator pane below volume (RSI/MACD). 0 = none. */
  subPaneFraction = 0;

  // -- layout ---------------------------------------------------------------

  get plotLeft(): number {
    return this.insets.left;
  }
  get plotRight(): number {
    return this.width - this.insets.right;
  }
  get plotWidth(): number {
    return Math.max(1, this.plotRight - this.plotLeft);
  }
  /** Total vertical space shared by price + volume + optional sub-pane. */
  get bodyTop(): number {
    return this.insets.top;
  }
  get bodyBottom(): number {
    return this.height - this.insets.bottom;
  }
  get bodyHeight(): number {
    return Math.max(1, this.bodyBottom - this.bodyTop);
  }

  get subPaneHeight(): number {
    return this.subPaneFraction > 0 ? this.bodyHeight * this.subPaneFraction : 0;
  }
  get volumeHeight(): number {
    return (this.bodyHeight - this.subPaneHeight) * VOLUME_FRACTION;
  }
  get priceHeight(): number {
    return Math.max(1, this.bodyHeight - this.subPaneHeight - this.volumeHeight);
  }
  get priceTop(): number {
    return this.bodyTop;
  }
  get priceBottom(): number {
    return this.bodyTop + this.priceHeight;
  }
  get volumeTop(): number {
    return this.priceBottom;
  }
  get volumeBottom(): number {
    return this.volumeTop + this.volumeHeight;
  }
  get subPaneTop(): number {
    return this.volumeBottom;
  }
  get subPaneBottom(): number {
    return this.bodyBottom;
  }

  get barWidth(): number {
    return this.plotWidth / this.barsVisible;
  }

  /** Drawn candle body width — always odd-ish and at least 1px. */
  get candleWidth(): number {
    const w = this.barWidth;
    if (w <= 2) return 1;
    return Math.max(1, Math.floor(w * 0.72));
  }

  // -- x mapping ------------------------------------------------------------

  /** Pixel x of the *centre* of bar `index`. */
  xOf(index: number): number {
    return this.plotLeft + (index - this.offset + 0.5) * this.barWidth;
  }

  /** Fractional bar index at pixel x. */
  indexAt(px: number): number {
    return (px - this.plotLeft) / this.barWidth + this.offset - 0.5;
  }

  get firstVisible(): number {
    return Math.floor(this.offset);
  }
  get lastVisible(): number {
    return Math.ceil(this.offset + this.barsVisible);
  }

  // -- y mapping ------------------------------------------------------------

  yOf(price: number): number {
    const top = this.priceTop;
    const h = this.priceHeight;
    if (this.logScale) {
      const lo = Math.log(Math.max(1e-12, this.priceMin));
      const hi = Math.log(Math.max(1e-12, this.priceMax));
      const v = Math.log(Math.max(1e-12, price));
      return top + h - ((v - lo) / (hi - lo || 1)) * h;
    }
    return top + h - ((price - this.priceMin) / (this.priceMax - this.priceMin || 1)) * h;
  }

  priceAt(py: number): number {
    const top = this.priceTop;
    const h = this.priceHeight;
    const t = (top + h - py) / (h || 1);
    if (this.logScale) {
      const lo = Math.log(Math.max(1e-12, this.priceMin));
      const hi = Math.log(Math.max(1e-12, this.priceMax));
      return Math.exp(lo + t * (hi - lo));
    }
    return this.priceMin + t * (this.priceMax - this.priceMin);
  }

  /** Volume pane y for a volume value given the pane's own max. */
  yOfVolume(v: number, maxVolume: number): number {
    const h = this.volumeHeight;
    if (maxVolume <= 0) return this.volumeBottom;
    // Leave a 12% headroom so the tallest bar does not touch the price pane.
    return this.volumeBottom - (v / maxVolume) * h * 0.88;
  }

  // -- interaction ----------------------------------------------------------

  /** Zoom keeping the bar under `px` pinned in place. */
  zoomAt(px: number, factor: number, seriesLength: number): void {
    const anchor = this.indexAt(px);
    const next = clamp(this.barsVisible * factor, 12, 3000);
    if (next === this.barsVisible) return;
    const ratio = (anchor - this.offset) / this.barsVisible;
    this.barsVisible = next;
    this.offset = anchor - ratio * next;
    this.clamp(seriesLength);
  }

  panBy(dxPixels: number, seriesLength: number): void {
    this.offset -= dxPixels / this.barWidth;
    this.following = false;
    this.clamp(seriesLength);
  }

  /**
   * Keep the viewport somewhere sensible.
   *
   * Deliberately permissive on both edges: a chart that refuses to scroll past
   * the newest candle feels stuck, and traders expect empty space on the right
   * to draw into. We allow 40% of a screen of whitespace either side.
   */
  clamp(seriesLength: number): void {
    const pad = this.barsVisible * 0.4;
    const minOffset = -pad;
    const maxOffset = Math.max(minOffset, seriesLength - this.barsVisible + pad);
    this.offset = clamp(this.offset, minOffset, maxOffset);
    if (this.following) this.offset = Math.max(minOffset, seriesLength - this.barsVisible + this.rightPad);
  }

  /** Bars of empty space kept to the right of the newest candle. */
  rightPad = 8;

  snapToRight(seriesLength: number): void {
    this.following = true;
    this.offset = seriesLength - this.barsVisible + this.rightPad;
  }

  /** True when the newest bar is on screen, so "follow" can re-engage. */
  isAtRight(seriesLength: number): boolean {
    return this.offset + this.barsVisible >= seriesLength - 1;
  }

  setManualRange(min: number, max: number): void {
    this.autoScale = false;
    this.manualMin = min;
    this.manualMax = max;
    this.priceMin = min;
    this.priceMax = max;
  }

  resetScale(): void {
    this.autoScale = true;
  }

  /** Drag on the price axis: expand/compress the range about its centre. */
  scaleY(factor: number): void {
    const mid = (this.priceMax + this.priceMin) / 2;
    const half = ((this.priceMax - this.priceMin) / 2) * factor;
    this.setManualRange(mid - half, mid + half);
  }

  /** Drag inside the plot with the price axis locked: shift the range. */
  shiftY(dyPixels: number): void {
    const perPixel = (this.priceMax - this.priceMin) / this.priceHeight;
    const d = dyPixels * perPixel;
    this.setManualRange(this.priceMin + d, this.priceMax + d);
  }

  // -- autoscale ------------------------------------------------------------

  /**
   * Fit the price range to what is visible.
   *
   * `extraMin`/`extraMax` let overlays that live in price space (Bollinger
   * bands, a moving average that has run away from price, an alert line) pull
   * the range open so they are not clipped off the top of the chart.
   */
  autoFit(series: KlineSeries, extraMin = NaN, extraMax = NaN): void {
    if (!this.autoScale) {
      this.priceMin = this.manualMin;
      this.priceMax = this.manualMax;
      return;
    }
    const n = series.length;
    if (n === 0) {
      this.priceMin = 0;
      this.priceMax = 1;
      return;
    }
    const lo = Math.max(0, this.firstVisible);
    const hi = Math.min(n - 1, this.lastVisible);
    if (hi < lo) {
      this.priceMin = 0;
      this.priceMax = 1;
      return;
    }

    const highs = series.high.data;
    const lows = series.low.data;
    let min = Infinity;
    let max = -Infinity;
    for (let i = lo; i <= hi; i++) {
      const h = highs[i];
      const l = lows[i];
      if (h > max) max = h;
      if (l < min) min = l;
    }
    if (Number.isFinite(extraMin) && extraMin < min) min = extraMin;
    if (Number.isFinite(extraMax) && extraMax > max) max = extraMax;

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      this.priceMin = 0;
      this.priceMax = 1;
      return;
    }
    if (max === min) {
      const pad = Math.abs(max) * 0.001 || 1;
      min -= pad;
      max += pad;
    }

    const pad = (max - min) * 0.08;
    this.priceMin = min - pad;
    this.priceMax = max + pad;
    if (this.logScale && this.priceMin <= 0) this.priceMin = min * 0.98;
  }

  /** Largest volume among visible bars, for the volume pane's own scale. */
  visibleMaxVolume(series: KlineSeries): number {
    const n = series.length;
    if (n === 0) return 1;
    const lo = Math.max(0, this.firstVisible);
    const hi = Math.min(n - 1, this.lastVisible);
    const v = series.volume.data;
    let max = 0;
    for (let i = lo; i <= hi; i++) if (v[i] > max) max = v[i];
    return max || 1;
  }

  // -- axis ticks -----------------------------------------------------------

  /** Price gridlines at "nice" values, roughly every `targetPx` pixels. */
  priceTicks(targetPx = 56, out: number[] = []): number[] {
    out.length = 0;
    const h = this.priceHeight;
    if (h <= 0) return out;
    const count = Math.max(2, Math.floor(h / targetPx));

    if (this.logScale) {
      // Log axis: step in log space, then snap each label to a nice mantissa.
      const lo = Math.log10(Math.max(1e-12, this.priceMin));
      const hi = Math.log10(Math.max(1e-12, this.priceMax));
      const step = (hi - lo) / count;
      for (let i = 0; i <= count; i++) {
        const v = Math.pow(10, lo + step * i);
        const mag = Math.pow(10, Math.floor(Math.log10(v)));
        out.push(Math.round(v / (mag / 10)) * (mag / 10));
      }
      return out;
    }

    const step = niceStep((this.priceMax - this.priceMin) / count);
    const start = Math.ceil(this.priceMin / step) * step;
    for (let v = start; v <= this.priceMax; v += step) {
      // Re-round each step: repeated addition of a float accumulates error
      // that shows up as "43250.000000000004" in an axis label.
      out.push(Math.round(v / step) * step);
    }
    return out;
  }

  /**
   * Decimal places needed so adjacent price labels are distinguishable.
   *
   * Exactly what the tick step requires and no more. Padding this by one — as
   * an earlier version did — renders "78,900.0" where "78,900" was meant, and
   * that extra glyph is what overflows the axis gutter and clips the label.
   */
  priceTickDecimals(basePrecision: number): number {
    const count = Math.max(2, Math.floor(this.priceHeight / 56));
    const step = niceStep((this.priceMax - this.priceMin) / count);
    if (!(step > 0)) return basePrecision;
    const needed = Math.max(0, Math.ceil(-Math.log10(step)));
    return clamp(Math.min(needed, basePrecision), 0, 8);
  }

  /**
   * Time axis ticks as bar indices, spaced at least `targetPx` apart and
   * aligned to human-friendly time boundaries where possible.
   */
  timeTicks(series: KlineSeries, targetPx = 92, out: number[] = []): number[] {
    out.length = 0;
    const n = series.length;
    if (n === 0) return out;
    const minBars = Math.max(1, Math.ceil(targetPx / this.barWidth));

    // Prefer a step that is a round number of bars *and* lands on round times.
    const candidates = [1, 2, 5, 10, 15, 20, 30, 60, 120, 240, 480, 720, 1440];
    let step = candidates[candidates.length - 1];
    for (const c of candidates) {
      if (c >= minBars) {
        step = c;
        break;
      }
    }

    const lo = Math.max(0, this.firstVisible);
    const hi = Math.min(n - 1, this.lastVisible);
    const first = Math.ceil(lo / step) * step;
    for (let i = first; i <= hi; i += step) out.push(i);
    return out;
  }
}
